import { useCallback, useMemo, useState } from "react";
import { validateEmail, validateIsEmpty, validatePassword, generateRandomString } from '@src/utils/validation';

type RegistrationFormType = {
  acceptTerm: boolean
  acceptPolicy: boolean
  firstName: string
  lastName: string
  email: string
  password: string
  rePassword: string
  school: string
  planOptions: string[]
  userRoleDesc: string
}
const defaultForm: RegistrationFormType = {
  acceptTerm: false,
  acceptPolicy: false,
  firstName: '',
  lastName: '',
  email: '',
  password: '',
  rePassword: '',
  school: '',
  planOptions: [],
  userRoleDesc: ''
}

export function useRegistrationForm (type: 'Account' | 'School' | 'User') {
  const [form, setForm] = useState<RegistrationFormType>(defaultForm)
  const handleFieldChange = useCallback((field: string, value: string | number | boolean | string[]) => {
    setForm({ ...form, [field]: value })
  }, [form])
  const formErrors = useMemo(() => {
    const errors: Record<string, string> = {}
    if (validateIsEmpty(form.firstName)) {
      errors['firstName'] = 'Please fill in your first name.'
    } else {
      errors['firstName'] = ''
    }
    if (validateIsEmpty(form.lastName)) {
      errors['lastName'] = 'Please fill in your last name.'
    } else {
      errors['lastName'] = ''
    }
    errors['email'] = validateEmail(form.email) ?? ''
    errors['password'] = validatePassword(form.password) ?? ''
    errors['rePassword'] = validatePassword(form.rePassword) ?? ''
    if (form.password !== form.rePassword) {
      errors['rePassword'] = 'Please make sure that passwords are equal.'
    }
    if (!form.acceptTerm) {
      errors['acceptTerm'] = 'Please accept the Terms to proceed.'
    } else {
      errors['acceptTerm'] = ''
    }
    if (!form.acceptPolicy) {
      errors['acceptPolicy'] = 'Please accept the Privacy Policy.'
    } else {
      errors['acceptPolicy'] = ''
    }
    if (type === 'Account') {
      if (!form.school) {
        errors['school'] = 'Please select your school.'
      } else {
        errors['school'] = ''
      } 
    } else {
      errors['school'] = ''
    }

    return errors
  }, [form, type])

  const resetForm = useCallback(() => {
    setForm(defaultForm)
  }, [])

  const populateForm = useCallback((socialUserInfo: { email: string; family_name: string; given_name: string }) => {
    setForm((_form) => {
      const randomVal = generateRandomString(10)
      return {
        ..._form,
        email: socialUserInfo.email,
        firstName: socialUserInfo.given_name,
        lastName: socialUserInfo.family_name,
        password: randomVal,
        rePassword: randomVal,
      }
    })
  }, [])

  return { form, formErrors, resetForm, populateForm, handleFieldChange }
}

export function transformToAccountForm(form: RegistrationFormType) {
  return {
    firstName: form.firstName,
    lastName: form.lastName,
    email: form.email,
    school: form.school,
    usages: form.planOptions,
    password: form.password,
    roleDesc: form.userRoleDesc,
  }
}
